import {OrdersView} from "../views/orders";
import View, {Notification} from "../framework/view";
import {AppFunction, FunctionBadge, JsonDate, Template} from "../misc/types";
import {PrintOrdersView} from "../views/print_orders";
import {ViewOrderView} from "../views/view_order";
import {ScanView} from "../views/scan";
import {App} from "../app/app";
import {AddOrderView} from "../views/add_order";
import {MapOrdersView} from "../views/map_orders";
import {ViewOrderImageView} from "../views/view_order_image";
import blank from "../../pugc/blank-page";
import {todayJSONDate} from "../misc/dates";
import {EditOrderView} from "../views/edit_order";
import {NumberView} from "../views/number";
import {AddPaymentView} from "../views/add_payment";
import LocalDB from "../misc/localdb";
import {ListOrdersView} from "../views/list_orders";
import {ListActivityView} from "../views/list_activity";
import {InvoiceView} from "../views/invoice";
import {DeliveryMapView} from "../views/delivery_map";
import {CardsView} from "../views/cards";

export class OrdersFunction extends AppFunction {

    readonly v : OrdersView;

    constructor() {
        super();
        this.v = new OrdersView(this.constructor.name, false);
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders`;
    }

    isCacheable(): boolean {
        return true;
    }

    refresh() {
        this.v.refresh(false);
    }

}

export class OrdersTodayFunction extends AppFunction {

    readonly v : OrdersView;

    constructor(status?: string) {
        super();
        const today = todayJSONDate();
        this.v = new OrdersView(this.constructor.name, false, today);
        if (status) {
            this.v.setState(status);
        }
        this.v.setPersistentSync(this);
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/today`;
    }

    isCacheable(): boolean {
        return true;
    }

    refresh() {
        const today = todayJSONDate();
        this.v.setToday(today);
        this.v.refresh(false);
    }

}

export class NewOrdersFunction extends AppFunction {

    readonly v : OrdersView;

    constructor() {
        super();
        this.v = new OrdersView(this.constructor.name, true);
        this.v.setPersistentSync(this);
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/new`;
    }

    refresh() {
        this.v.refresh(false);
    }

    isCacheable(): boolean {
        return true;
    }

}

export class NewOrdersForPickupFunction extends AppFunction {

    readonly v : OrdersView;

    readonly dates : JsonDate[];

    constructor(dates: JsonDate[]) {
        super();
        this.dates = dates;
        this.v = new OrdersView(this.constructor.name, true);
        this.v.setDelivery("Pickup");
        this.v.setToday(dates[0]);
        this.v.setExcludedUndated();
        this.v.setPersistentSync(this);
        dates.forEach((date) => this.v.addDuedate(date));
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/new/pickups/${this.dates.join(",")}`;
    }

    refresh() {
        this.v.refresh(false);
    }

    isCacheable(): boolean {
        return true;
    }

}

export class NewOrdersForDeliveryFunction extends AppFunction {

    readonly v : OrdersView;

    readonly dates : JsonDate[];

    constructor(dates: JsonDate[]) {
        super();
        this.dates = dates;
        this.v = new OrdersView(this.constructor.name, true);
        this.v.setDelivery("Delivery");
        this.v.setToday(dates[0]);
        this.v.setExcludedUndated();
        dates.forEach((date) => this.v.addDuedate(date));
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/new/deliveries/${this.dates.join(",")}`;
    }

    refresh() {
        this.v.refresh(false);
    }

}

export class AllManualOrdersFunction extends AppFunction {

    readonly v : OrdersView;

    readonly dates : JsonDate[];

    constructor(dates: JsonDate[]) {
        super();
        this.dates = dates;
        this.v = new OrdersView(this.constructor.name, false);
        this.v.setFilter((order) => {
            if (! order.id) {
                return false;
            }
            if (! order.id.startsWith("manual-")) {
                return false;
            }
            return true;
        });
        this.v.setToday(dates[0]);
        this.v.configForEvent();
        this.v.setShowInventory(true);
        this.v.setPersistentSync(this);
        dates.forEach((date) => this.v.addDuedate(date));
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/all/manual/${this.dates.join(",")}`;
    }

    refresh() {
        this.v.refresh(false);
    }

    isCacheable(): boolean {
        return true;
    }

}

export class AllOrdersForDeliveryFunction extends AppFunction {

    readonly v : OrdersView;

    readonly dates : JsonDate[];

    constructor(dates: JsonDate[]) {
        super();
        this.dates = dates;
        this.v = new OrdersView(this.constructor.name, false);
        this.v.setDelivery("Delivery");
        this.v.setToday(dates[0]);
        this.v.configForEvent();
        this.v.setPersistentSync(this);
        dates.forEach((date) => this.v.addDuedate(date));
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/all/deliveries/${this.dates.join(",")}`;
    }

    refresh() {
        this.v.refresh(false);
    }

    isCacheable(): boolean {
        return true;
    }

}

export class PrintOrdersFunction extends AppFunction {

    readonly v : View;

    ids: string[];

    constructor(ids: string[]) {
        super();
        this.ids = ids;
        this.v = new PrintOrdersView(ids);
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/print/` + this.ids.join(",");
    }

}

export class CardFunction extends AppFunction {

    readonly v : View;

    ids: string[];

    constructor(ids: string[]) {
        super();
        this.ids = ids;
        this.v = new CardsView(ids);
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/card/` + this.ids.join(",");
    }

}

export class ListOrdersFunction extends AppFunction {

    readonly v : View;

    ids: string[];

    constructor(ids?: string[]) {
        super();
        this.ids = lastListIds(ids);
        this.v = new ListOrdersView(this.ids);
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/list/` + this.ids.join(",");
    }

}

function lastMapIds(ids?: string[]) : string[]  {
    const db = LocalDB.instance();
    const key = "MapOrdersFunction.lastMapIds";
    if (ids) {
        db.set(key, ids);
        console.log(`saved ${ids.length} lastMapIds`);
    }
    return (db.get(key) as string[]) || [];
}

export function lastListIds(ids?: string[]) : string[]  {
    const db = LocalDB.instance();
    const key = "ListOrdersFunction.lastListIds";
    if (ids) {
        db.set(key, ids);
        console.log(`saved ${ids.length} lastListIds`);
    }
    return (db.get(key) as string[]) || [];
}


export class MapOrdersFunction extends AppFunction {

    readonly v : View;

    ids: string[];

    constructor(ids?: string[]) {
        super();
        this.ids = ids ? ids : lastMapIds();
        this.v = new MapOrdersView(this.ids);
        console.log(`construct MapOrdersView with ${this.ids ? this.ids.length : 0} orders`)
        lastMapIds(this.ids);
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/map/` + this.ids.join(",");
    }

    isCacheable() {
        return true;
    }

}

export class DeliveryMapFunction extends AppFunction {

    readonly v : DeliveryMapView;

    readonly dates : JsonDate[];

    constructor(dates: JsonDate[]) {
        super();
        this.dates = dates;
        this.v = new DeliveryMapView(dates);
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/map/deliveries/${this.dates.join(",")}`;
    }

    refresh() {
        this.v.refresh();
    }

}

export class ViewOrderFunction extends AppFunction {

    readonly v : View;

    id: string;
    constructor(app: App, id: string) {
        super();
        this.id = id;
        this.v = new ViewOrderView(app, id);
    }
    view() {
        return this.v;
    }
    url() {
        return `/orders/view/` + this.id;
    }

}

export class ViewOrderImageFunction extends AppFunction {

    readonly v : View;

    id: string;
    constructor(id: string) {
        super();
        this.id = id;
        this.v = new ViewOrderImageView(id);
    }
    view() {
        return this.v;
    }
    url() {
        return `/img/` + this.id;
    }

    pageTemplate(): Template | undefined {
        return blank;
    }
}

export class AddOrderFunction extends AppFunction {

    readonly v : View;

    constructor(app: App) {
        super();
        this.v = new AddOrderView(app);
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/add`;
    }
}

export class AddPaymentFunction extends AppFunction {

    readonly v : View;

    constructor(app: App) {
        super();
        this.v = new AddPaymentView(app);
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/add/payment`;
    }
}

export class ScanFunction extends AppFunction {

    readonly v : View;

    constructor() {
        super();
        this.v = new ScanView();
    }

    view() {
        return this.v;
    }

    url() {
        return `/scan`;
    }

}

export class NumberFunction extends AppFunction {

    readonly v : View;

    constructor() {
        super();
        this.v = new NumberView();
    }

    view() {
        return this.v;
    }

    url() {
        return `/number`;
    }

}

export class EditOrderFunction extends AppFunction {

    readonly v : View;

    id: string;
    constructor(app: App, id: string) {
        super();
        this.id = id;
        this.v = new EditOrderView(app, id);
    }
    view() {
        return this.v;
    }
    url() {
        return `/orders/edit/` + this.id;
    }

}

export class ListActivityFunction extends AppFunction {

    readonly v : View;

    constructor() {
        super();
        this.v = new ListActivityView();
    }

    view() {
        return this.v;
    }

    url() {
        return `/activity`;
    }

}

export class InvoiceFunction extends AppFunction {

    readonly v : View;

    private id: string;

    constructor(id: string) {
        super();
        this.id = id;
        this.v = new InvoiceView(this.id);
    }

    view() {
        return this.v;
    }

    url() {
        return `/orders/invoice/` + this.id;
    }

    isCacheable() {
        return true;
    }

}
